"use client";

import * as React from "react";
import {
  AlertCircle,
  AlertTriangle,
  CalendarX,
  Info,
  Lock,
  RefreshCw,
  Sparkles,
  XCircle,
} from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import {
  type BasketUnavailability,
  defaultUnavailabilityMessage,
  getUnavailabilityBucket,
} from "@obby/lib";

type Tone = "destructive" | "warning" | "info" | "muted";

type Variant = {
  tone: Tone;
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  body?: string;
};

function variantFor(u: BasketUnavailability): Variant {
  switch (u.code) {
    case "SESSION_PARTIAL_CAPACITY":
      return {
        tone: "warning",
        Icon: AlertTriangle,
        title: `Only ${u.availableQuantity} ${u.availableQuantity === 1 ? "spot" : "spots"} left`,
        body: `You picked ${u.requestedQuantity}. Reduce to ${u.availableQuantity} to keep this in your basket.`,
      };
    case "PRODUCT_PARTIAL_STOCK":
      return {
        tone: "warning",
        Icon: AlertTriangle,
        title: `Only ${u.availableQuantity} in stock`,
        body: `You picked ${u.requestedQuantity}. Reduce the quantity to continue.`,
      };
    case "SESSION_SOLD_OUT":
      return { tone: "destructive", Icon: XCircle, title: "Sold out" };
    case "PRODUCT_OUT_OF_STOCK":
      return { tone: "destructive", Icon: XCircle, title: "Out of stock" };
    case "SESSION_STARTED":
      return {
        tone: "muted",
        Icon: CalendarX,
        title: "This session has already started",
      };
    case "BOOKING_CUTOFF":
      return {
        tone: "muted",
        Icon: CalendarX,
        title: "Bookings have closed for this session",
        body: `This session stops taking bookings ${u.closesHoursBefore} ${u.closesHoursBefore === 1 ? "hour" : "hours"} before it starts.`,
      };
    case "TICKET_SALE_ENDED":
      return {
        tone: "muted",
        Icon: CalendarX,
        title: "Ticket sales have closed",
        body: `This ticket stops selling ${u.closesHoursBefore} ${u.closesHoursBefore === 1 ? "hour" : "hours"} before the session starts.`,
      };
    case "SESSION_CANCELLED":
      return {
        tone: "destructive",
        Icon: AlertCircle,
        title: "The school cancelled this session",
        body: "We're sorry for the inconvenience.",
      };
    case "COURSE_UNAVAILABLE":
      return {
        tone: "destructive",
        Icon: AlertCircle,
        title: "No longer offered",
      };
    case "PRODUCT_DISABLED":
      return { tone: "destructive", Icon: AlertCircle, title: "No longer available" };
    case "BUNDLE_DISABLED":
      return { tone: "destructive", Icon: AlertCircle, title: "Bundle no longer available" };
    case "SESSION_NOT_FOUND":
    case "COURSE_NOT_FOUND":
    case "PRODUCT_NOT_FOUND":
    case "BUNDLE_NOT_FOUND":
      return { tone: "muted", Icon: Info, title: "No longer offered" };
    case "MEMBERSHIP_INACTIVE":
      return {
        tone: "warning",
        Icon: Lock,
        title: "Your membership is inactive",
        body: "Renew to keep this item.",
      };
    case "TICKET_MEMBERS_ONLY":
      return {
        tone: "info",
        Icon: Sparkles,
        title: "Members only",
        body: "Join to unlock this ticket.",
      };
    case "TICKET_TAG_GATED":
      return {
        tone: "muted",
        Icon: Lock,
        title: "Not available on this account",
        body: "This ticket is limited to selected customers.",
      };
    case "TICKET_CONSTRAINT_VIOLATION":
      return {
        tone: "warning",
        Icon: AlertTriangle,
        title: "Doesn't meet this ticket's rules",
        body: u.reason,
      };
    // Per-customer trial allowance. Without this case it fell to the generic
    // default below, so the Next widget — which has no banner of its own —
    // never said why the item was blocked or what to do about it.
    case "TRIAL_LIMIT_REACHED":
      return {
        tone: "warning",
        Icon: Lock,
        title: "Trial limit reached",
        body: `${defaultUnavailabilityMessage(u)}. Remove it, or book a regular ticket instead.`,
      };
    case "MULTIPLE_SCHOOLS":
      return {
        tone: "warning",
        Icon: AlertTriangle,
        title: "Mixed-school basket",
        body: "Keep items from one school only to continue.",
      };
    case "SCHOOL_PAYMENT_NOT_READY":
      return {
        tone: "muted",
        Icon: Info,
        title: "Checkout temporarily unavailable",
        body: "Please contact the school.",
      };
    case "VALIDATION_TRANSIENT":
      return {
        tone: "warning",
        Icon: RefreshCw,
        title: "We couldn't check availability",
        // No promise of an automatic re-check: the widget polls, but the
        // webcomponents drawer validates when it OPENS and nothing else, so
        // the old second clause was false on that surface. Retry is wired on
        // both.
        body: "Tap retry to check again.",
      };
    case "UNKNOWN_ITEM_TYPE":
      return { tone: "muted", Icon: Info, title: "We couldn't recognise this item" };
    // Equipment/resource-pool refusals. Without these three cases they fell to
    // the default below and rendered as the generic "can't be booked right
    // now" — so the customer was told nothing about WHY, and could not tell a
    // sold-out class from sold-out equipment or an unbookable combination.
    case "RESOURCE_POOL_SOLD_OUT":
      return {
        tone: "destructive",
        Icon: AlertCircle,
        title: "The equipment for this booking has just been taken",
        body: "Try another time, or reduce the number of tickets.",
      };
    case "POOL_TICKET_NOT_BUNDLEABLE":
      return {
        tone: "destructive",
        Icon: AlertCircle,
        title: "This ticket can't be bought as part of a bundle",
        body: "Book it on its own instead.",
      };
    case "APPOINTMENT_SLOT_TAKEN":
      return {
        tone: "destructive",
        Icon: AlertCircle,
        title: "That appointment slot has just been taken",
        body: "Pick another time to continue.",
      };
    // Runtime guard: a bundle compiled before a new code was added can
    // receive it from a newer API. Without this, `variant.tone` below throws
    // and takes down the whole basket UI.
    default:
      return {
        tone: "destructive",
        Icon: AlertCircle,
        title: "This item can't be booked right now",
      };
  }
}

const TONE_CLASSES: Record<
  Tone,
  { container: string; text: string; iconText: string }
> = {
  destructive: {
    container: "bg-destructive/10 border-destructive/20",
    text: "text-destructive",
    iconText: "text-destructive",
  },
  warning: {
    container: "bg-amber-100/60 border-amber-200",
    text: "text-amber-900",
    iconText: "text-amber-700",
  },
  info: {
    container: "bg-blue-100/60 border-blue-200",
    text: "text-blue-900",
    iconText: "text-blue-700",
  },
  muted: {
    container: "bg-muted border-border",
    text: "text-foreground",
    iconText: "text-muted-foreground",
  },
};

export type BasketItemStatusProps = {
  unavailability: BasketUnavailability;
  /** Called when the user clicks the primary auto-fix action (Reduce to N, Retry, Join). */
  onPrimaryAction?: () => void;
  /** Called when the user clicks Remove. */
  onRemove?: () => void;
  /** Called when the user clicks the bucket-specific secondary action (e.g. "See other sessions"). */
  onSecondaryAction?: () => void;
  /** Optional override for the primary action label (defaults to bucket-specific). */
  primaryActionLabel?: string;
  className?: string;
};

function BasketItemStatus({
  unavailability,
  onPrimaryAction,
  onRemove,
  onSecondaryAction,
  primaryActionLabel,
  className,
}: BasketItemStatusProps) {
  const variant = variantFor(unavailability);
  const bucket = getUnavailabilityBucket(unavailability);
  const tone = TONE_CLASSES[variant.tone];
  const { Icon } = variant;

  let primaryLabel: string | null = null;
  if (
    bucket === "partial" &&
    (unavailability.code === "SESSION_PARTIAL_CAPACITY" ||
      unavailability.code === "PRODUCT_PARTIAL_STOCK")
  ) {
    primaryLabel = `Reduce to ${unavailability.availableQuantity}`;
  } else if (bucket === "transient") {
    primaryLabel = "Retry";
  } else if (
    bucket === "members_only" &&
    unavailability.code === "TICKET_MEMBERS_ONLY"
  ) {
    primaryLabel = "Join membership";
  }
  if (primaryActionLabel) primaryLabel = primaryActionLabel;

  // The label is derived from the bucket alone, so it exists even when the
  // host app supplied no handler for it — see `resolvePrimaryAction`, which
  // returns undefined precisely so that button stays off the screen. Both
  // halves have to agree, or the row below reserves space for a button that
  // is never rendered.
  const showPrimary = !!primaryLabel && !!onPrimaryAction;

  // Remove is hidden when binning the item is the wrong lesson: a backend
  // hiccup (VALIDATION_TRANSIENT) clears itself, and a school-side error is
  // not the buyer's fault. The card's own trash-icon button is unaffected —
  // this is only the labelled Remove inside the panel.
  //
  // Keyed on the CODE. That was originally because UNKNOWN_ITEM_TYPE shared
  // the `transient` bucket and needed the opposite treatment — it has since
  // moved to `gone`, so the two forms are now equivalent. Kept as the code
  // because the rule is about retryability, not about the bucket: a future
  // code landing in `transient` should have to opt out of Remove explicitly.
  const showRemove =
    unavailability.code !== "VALIDATION_TRANSIENT" &&
    bucket !== "school_error" &&
    !!onRemove;

  // Mirrors the button's own `bucket === "cancelled"` guard below. Without the
  // bucket here, an item whose host passed `onSeeAlternatives` opened the row
  // for a secondary button that then declined to render.
  const showSecondary = !!onSecondaryAction && bucket === "cancelled";

  return (
    <div className={cn("border-t px-3 py-2", tone.container, className)}>
      <div className="flex items-start gap-2">
        <Icon className={cn("h-4 w-4 shrink-0 mt-0.5", tone.iconText)} />
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium", tone.text)}>
            {variant.title}
          </p>
          {variant.body && (
            <p className={cn("text-sm", tone.text)}>{variant.body}</p>
          )}
          {(showPrimary || showRemove || showSecondary) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {showPrimary && (
                <Button
                  variant="default"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={onPrimaryAction}
                >
                  {primaryLabel}
                </Button>
              )}
              {showRemove && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={onRemove}
                >
                  Remove
                </Button>
              )}
              {showSecondary && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={onSecondaryAction}
                >
                  See other sessions
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { BasketItemStatus };
